// connectCall — mint a LiveKit token, build a Room, connect, wire events.
// Called from RoomList.MeetRoomRow onClick, Main.Join onClick, NewRoomModal
// submit, etc — anywhere the user enters a call view.
//
// Kept as a registered project function (not a page-level lifecycle hook)
// because DOMQL v3.14 doesn't fire onCreate on `if:`/`extends:` children and
// doesn't fire onUpdate on root state changes. Chat/calendar follow the
// same "imperative action functions, no lifecycle hooks on sub-views"
// pattern.
//
// LiveKit Room instance is stored in a MODULE-LEVEL variable, NOT in
// smbls reactive state. Passing the Room through `root.update({...})`
// strips class methods (the reactive proxy serializes values), so
// `room.disconnect()` fails on subsequent reads. The module variable
// preserves the live instance with methods intact. Components/functions
// that need the Room import `getLivekitRoom()`.
import { buildLivekitRoom, preacquireLocalTracks, probeLivekitStatus } from './livekitConnect.js'
import { attachParticipantTracks, attachAllParticipantTracks } from './attachTracks.js'
import { rowToUtterance, livekitErrorFields } from './meetUtils.js'
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { workspaceApiBase } from '@symbo-ls/workspace-shared/functions/workspaceApiBase.js'
import { resolveLivekitPair, readPrewarmHost, rememberPrewarmHost } from './livekitHost.js'
import { applyTileGridLayout } from './tileGridLayout.js'
import { meetSameWorkspaceWriter } from './sameWorkspaceWriter.js'
// Direct import, not el.call — MEET-DISCONNECTCALL-UNSUBSCRIBE-CONTEXT-1.
// These two are pure internal wiring for the CALL-slot subscription this
// file owns end to end (no other caller anywhere in the app); resolving
// them through el.call made teardown depend on the INVOKING element's own
// DOMQL context, which registers them only after meet's full context has
// folded in (meetFold.js) — unlike connectCall/disconnectCall themselves,
// which ride the always-registered eager slice (eagerSlice.js). The
// shell's workspace-switch teardown calls disconnectCall through
// `root.__element`, whose context may never have folded meet in, so
// el.call('unsubscribeMeetCallRealtime') silently no-op'd there and the
// subscription leaked past the tenant boundary.
import { subscribeMeetCallRealtime, unsubscribeMeetCallRealtime } from './subscribeMeetRealtime.js'

let _livekitRoom = null

export const getLivekitRoom = () => _livekitRoom

// Join-phase telemetry — performance marks/measures so a slow join NAMES its
// slow phase (token mint vs module import vs getUserMedia vs connect) instead
// of being one opaque wait. Read via
// performance.getEntriesByType('measure').filter(m => m.name.startsWith('meetjoin:'))
// Same pattern as the wsboot:* marks in
// workspace/functions/loadPrivateData.js. Guarded: no-ops wherever the
// Performance API is absent.
const _mark = (name) => {
  try {
    if (typeof performance !== 'undefined') performance.mark(name)
  } catch (_) {
    /* telemetry only */
  }
}
const _measure = (name, from, to) => {
  try {
    if (typeof performance !== 'undefined') performance.measure(name, from, to)
  } catch (_) {
    /* telemetry only */
  }
}

// One-time page-unload hook — LiveKit's server marks an abruptly-closed
// tab as disconnected only after its connection timeout; an explicit
// disconnect() on beforeunload makes the participant leave immediately.
// Module flag guards double-registration (connectCall fires per join).
let _unloadHookInstalled = false
const _installUnloadHook = () => {
  if (_unloadHookInstalled || typeof window === 'undefined') return
  _unloadHookInstalled = true
  window.addEventListener('beforeunload', () => {
    try {
      _livekitRoom?.disconnect()
    } catch (err) {
      console.warn('[connectCall] unload disconnect failed', err)
    }
  })
}

// Filter out the meet-transcriber agent — LiveKit assigns it an identity
// like `agent-AJ_<jobId>` when AgentDispatchClient dispatches a named agent.
// It's listen-only (no tracks published) and should be invisible in the
// human-facing participant grid.
const _meetIsAgent = (identity) => typeof identity === 'string' && identity.startsWith('agent-')

// Detect a published screen-share track on a participant. LiveKit
// exposes the source enum as 'screen_share' on the publication; we
// also accept the camelCase form some versions emit. Used both for
// the tile list (one extra tile per active screen-share) and for the
// attach helper (selects the right pub by source).
const _hasScreenShare = (participant) => {
  const pubs =
    typeof participant?.videoTrackPublications?.values === 'function'
      ? [...participant.videoTrackPublications.values()]
      : Object.values(participant?.videoTrackPublications || {})
  return pubs.some(
    (p) => p && p.track && (p.source === 'screen_share' || p.source === 'screenShare')
  )
}

const _refreshParticipants = (root, livekitRoom) => {
  const local = livekitRoom.localParticipant
  const remoteValues =
    typeof livekitRoom.remoteParticipants?.values === 'function'
      ? livekitRoom.remoteParticipants.values()
      : Object.values(livekitRoom.remoteParticipants || {})
  const remote = Array.from(remoteValues).filter((p) => !_meetIsAgent(p.identity))
  // `id` must be stable so smbls can diff the children array and REUSE
  // the existing tile element instead of recreating it on every mic /
  // speaker event. Without a stable id, each refresh spawns fresh
  // <video> DOM nodes and the user sees a flicker every time anyone
  // speaks. Use participant.identity as the id — and append a ':screen'
  // suffix for the virtual screen-share tile so the camera tile and the
  // screen tile diff independently.
  const all = []
  all.push({
    id: local.identity,
    identity: local.identity,
    name: local.name || local.identity,
    isLocal: true,
    isMicEnabled: local.isMicrophoneEnabled,
    isCameraEnabled: local.isCameraEnabled,
    mediaSource: 'camera',
    isScreen: false
  })
  if (_hasScreenShare(local)) {
    all.push({
      id: local.identity + ':screen',
      identity: local.identity,
      name: (local.name || local.identity) + ' (screen)',
      isLocal: true,
      isMicEnabled: false, // screen-share tile never shows mic state
      isCameraEnabled: true, // suppress the camera-off fallback overlay
      mediaSource: 'screen_share',
      isScreen: true
    })
  }
  for (const p of remote) {
    all.push({
      id: p.identity,
      identity: p.identity,
      name: p.name || p.identity,
      isLocal: false,
      isMicEnabled: p.isMicrophoneEnabled,
      isCameraEnabled: p.isCameraEnabled,
      mediaSource: 'camera',
      isScreen: false
    })
    if (_hasScreenShare(p)) {
      all.push({
        id: p.identity + ':screen',
        identity: p.identity,
        name: (p.name || p.identity) + ' (screen)',
        isLocal: false,
        isMicEnabled: false,
        isCameraEnabled: true,
        mediaSource: 'screen_share',
        isScreen: true
      })
    }
  }
  root.update({ livekitParticipants: all })
  // Re-apply the grid templates + featured-duplicate hide — the TileGrid
  // has no reactive layout path in v3.14 (see tileGridLayout.js).
  applyTileGridLayout(root, livekitRoom)
}

// Active-speaker state lives in a separate slot so speaker-change events
// don't invalidate the participants array diff. Components that want a
// speaker ring border read from root.livekitSpeakers directly.
export const _refreshSpeakers = (root, livekitRoom) => {
  const speakers = livekitRoom.activeSpeakers || []
  const set = {}
  for (const p of speakers) set[p.identity] = true
  root.update({ livekitSpeakers: set })
  // Speaker changes flip featured/filmstrip mode — re-apply imperatively.
  applyTileGridLayout(root, livekitRoom)
}

export const connectCall = async function (roomId, mediaPrefs) {
  const root = this.state.root
  if (!roomId) roomId = root.activeRoomId
  if (!roomId) {
    console.error('[connectCall] no roomId')
    return null
  }
  // Media prefs from the join-preview confirm (MEET-AUTOJOIN-1). Absent →
  // both on, which keeps every non-preview call site (new-call start,
  // in-call retry/reconnect, live-singleton reattach) behaving as before.
  const media = { mic: mediaPrefs?.mic !== false, cam: mediaPrefs?.cam !== false }
  if (_livekitRoom) {
    // Same room → reuse the live connection. Different room → tear the
    // old one down through the normal disconnect path first (rescopes
    // realtime, clears participant state), then fall through to connect.
    const connectedId = _livekitRoom._smblsRoomId || _livekitRoom.name
    if (connectedId === roomId) {
      // Re-entering the call view for a room we never left (SPA navigation
      // keeps the module-level Room alive). The DOM tiles were torn down
      // with the page, so re-establish the UI side: re-scope realtime back
      // to this room (another page may have narrowed it), refresh the
      // participant state, and re-attach tracks once smbls has re-rendered
      // the tile DOM.
      console.log('[connectCall] already connected — reattaching UI')
      // CALL-slot subscription — pinned to this room for the life of the
      // call, immune to page-slot re-scopes (see subscribeMeetRealtime.js;
      // idempotent for the same room).
      subscribeMeetCallRealtime.call(this, roomId)
      root.update({ livekitConnected: true, meetCallInFlight: false })
      _refreshParticipants(root, _livekitRoom)
      const liveRoom = _livekitRoom
      requestAnimationFrame(() => attachAllParticipantTracks(liveRoom))
      return _livekitRoom
    }
    console.log('[connectCall] switching rooms', connectedId, '→', roomId)
    await this.call('disconnectCall')
  }
  if (root.meetCallInFlight) {
    console.log('[connectCall] already connecting')
    return null
  }

  root.update({
    meetCallInFlight: true,
    meetCallError: null,
    meetCallErrorName: null,
    meetCallErrorCode: null,
    meetCallErrorReason: null,
    meetCallErrorStatus: null,
    livekitConnected: false
  })

  // MEET-LIVE-CALL-SURVIVES-SWITCH-1 — tenant guard for every write that
  // outlives an await below. Captured HERE, on the same tick as the pre-flight
  // reset and before the first await, so it snapshots the workspace this join
  // was issued for (see functions/sameWorkspaceWriter.js: capturing it inside
  // the completion would snapshot the NEW workspace and never fire).
  //
  // ⛔ THIS GUARD IS ONLY CORRECT BECAUSE THE SWITCH PATH NOW DISCONNECTS
  // FIRST. It was deliberately withheld while `onWorkspaceChange` had no
  // `disconnectCall`: dropping the completion write alone would have left a
  // LIVE LiveKit connection with no state saying so — an invisible active call
  // in B, worse than a stale banner. The completion below therefore treats a
  // DROPPED write as "this connection was born into a dead tenant" and tears
  // the room down; it never just skips the update.
  const _write = meetSameWorkspaceWriter(root)

  // CALL-slot realtime — pinned to this room (transcripts + analyses +
  // waiting + rooms) for the WHOLE life of the call, owned here and torn
  // down only by disconnectCall. Previously this rode the single shared
  // page slot, so any page re-scope (visiting the room list mid-call,
  // opening another room's transcript) silently replaced it and live
  // captions + the transcript panel went dead (2026-07-29 per Nika "really
  // fix transcripts not showing up in realtime") — made routine by the
  // persistent-call dock, which keeps calls alive across navigation.
  subscribeMeetCallRealtime.call(this, roomId)

  // Pre-acquired local tracks (see the prewarm block below) — tracked
  // OUTSIDE the try so every abort path (cross-org redirect, connect
  // failure) can release the devices and kill the camera light.
  let preTracksPromise = null
  // Kept outside the try so the catch can re-probe the LiveKit edge with
  // the very credentials this attempt dialled (see probeLivekitStatus).
  let _tokenInfo = null
  const _stopPreTracks = () => {
    if (!preTracksPromise) return
    preTracksPromise.then((tracks) => {
      if (!Array.isArray(tracks)) return
      for (const t of tracks) {
        try {
          t.stop()
        } catch (_) {
          /* already stopped */
        }
      }
    })
  }

  try {
    _mark('meetjoin:start')
    // ── Parallel prewarm ────────────────────────────────────────────────
    // The join used to be fully serial: mint (network RTT) → import
    // livekit-client (~250KB gz parse) → connect → mic getUserMedia →
    // camera getUserMedia. Everything that doesn't need the token now
    // starts immediately and runs WHILE the mint round-trips:
    //   - the livekit-client module import + Room construction,
    //   - a DNS/TLS prewarm against the last-known LiveKit host
    //     (room.prepareConnection — the URL is env-stable, cached from the
    //     previous join; a cold cache just skips the prewarm),
    //   - the getUserMedia acquisition for the kinds the user confirmed
    //     (ONE combined permission prompt). Join policy is the preview's
    //     media prefs (MEET-AUTOJOIN-1) — default both-on for non-preview
    //     call sites; tracks are stopped on every abort path so no camera
    //     light leaks.
    _mark('meetjoin:mint-start')
    const tokenPromise = this.call('mintLivekitToken', roomId)
    const startingCount =
      (root.liveParticipants?.[roomId] || []).filter((p) => !p.leftAt).length || 1
    _mark('meetjoin:import-start')
    const roomPromise = buildLivekitRoom(startingCount).then((room) => {
      _mark('meetjoin:import-done')
      _measure('meetjoin:import', 'meetjoin:import-start', 'meetjoin:import-done')
      try {
        // Keyed by API base (livekitHost.js): a host cached while this
        // origin talked to ANOTHER environment is never prewarmed here.
        const cachedUrl = readPrewarmHost(window.localStorage, workspaceApiBase())
        // ⚠️ DO NOT PASS THE TOKEN AS A SECOND ARGUMENT. It looks like a free
        // optimisation — the SDK docstring recommends it, and with a token
        // LiveKit Cloud also picks the best edge region here. That is exactly
        // what makes it dangerous with a CACHED url.
        //
        // Measured against livekit-client 2.21.0 (`Room.prepareConnection`):
        //   * no token  → `fetch(url, {method:'HEAD'})` only. Pure DNS/TLS
        //     warm. No `regionUrlProvider`, no `this.regionUrl`, no retained
        //     state. A stale cached host warms the wrong DNS entry and is
        //     otherwise INERT — `connect()` below still dials
        //     `tokenInfo.livekitUrl`.
        //   * WITH a token → sets `this.regionUrl`, which OVERRIDES the
        //     connect target. A stale cached host would then genuinely
        //     redirect the connection — a token/URL split created on the
        //     client, the exact shape MEET-LIVEKIT-PROJECT-MISMATCH-1 hunted
        //     server-side and never found.
        //
        // The cache is also self-healing (`rememberPrewarmHost` below
        // rewrites it after every successful mint and reports a move), so
        // staleness only spans a URL change until the next completed join.
        // Both properties are why this is safe TODAY; adding the token
        // silently removes the first one.
        if (cachedUrl) room.prepareConnection(cachedUrl).catch(() => {})
      } catch (_) {
        /* prewarm only */
      }
      return room
    })
    _mark('meetjoin:media-start')
    preTracksPromise = preacquireLocalTracks(media).then(
      (tracks) => {
        _mark('meetjoin:media-acquired')
        _measure('meetjoin:media-acquire', 'meetjoin:media-start', 'meetjoin:media-acquired')
        return tracks
      },
      // Missing device / permission denied / no mediaDevices — the
      // post-connect fallback path re-runs the per-device taxonomy.
      () => null
    )

    const tokenInfo = await tokenPromise
    _tokenInfo = tokenInfo
    _mark('meetjoin:mint-done')
    _measure('meetjoin:mint', 'meetjoin:mint-start', 'meetjoin:mint-done')
    // Cross-org room: the mint succeeds on MEMBERSHIP (the server's access
    // gate spans every org the caller belongs to), but this TAB can be
    // scoped to a DIFFERENT org — a bare or foreign-prefixed invite link.
    // Joining here would run the call under the wrong tenant: the room
    // record, participants and transcripts are workspace-scoped reads and
    // all come back empty (live 2026-07-29: a system-org invite opened as
    // /w/natali/meet — user "joined into natali account", no transcripts).
    // Redirect to the room's OWNING org URL instead — a FULL navigation on
    // purpose: switching orgs must run the whole boot pipeline (org pin,
    // workspace resolve, module load, claim convergence), which SPA nav
    // cannot; the hash re-seeds this exact join on arrival. Same class of
    // hard-navigation as signoutAndRedirect.
    //
    // ID-SPACE DISTINCTION (do not regress): `tokenInfo.workspaceId` is the
    // room's WORKSPACE id (meet_rooms.workspace_id) — NOT an org id. Comparing
    // it against root.org ids never matches, which silently disabled this
    // redirect (verified live on dev.my). The server resolves the owning ORG
    // from the Workspace doc and returns it as `organizationId` (null for
    // legacy rooms whose workspace_id resolves to no Workspace) — that is the
    // only value comparable against root.org / root.orgs.
    const _roomOrg = tokenInfo?.organizationId ? String(tokenInfo.organizationId) : null
    const _activeOrg = String(root.org?.id || root.org?._id || '')
    if (_roomOrg && _activeOrg && _roomOrg !== _activeOrg) {
      const _owning = (root.orgs || []).find((o) => String(o.id || o._id || '') === _roomOrg)
      const _slug = _owning && (_owning.slug || _owning.username)
      if (_slug && typeof window !== 'undefined') {
        console.warn('[connectCall] room belongs to another org — redirecting to', _slug)
        _stopPreTracks()
        root.update({ meetCallInFlight: false })
        // Carry the precise workspace too: boot's workspace resolution
        // accepts a workspace id as the ws-handle fallback, so the redirect
        // lands on the room's exact workspace, not just the org's HQ.
        const _ws = tokenInfo?.workspaceId ? `?ws=${tokenInfo.workspaceId}` : ''
        window.location.assign(
          `${window.location.origin}/w/${_slug}/meet${_ws}#room=${roomId}&view=call`
        )
        return null
      }
      // No membership row for the owning org resolved client-side — proceed
      // with the join (the server already authorized it) rather than strand
      // the user; the call works, only workspace-scoped chrome is degraded.
    }
    // One response, one pair: the token is only valid for the host it was
    // signed with (livekitHost.js). An incomplete or split response refuses
    // here with a named reason instead of reaching the SDK as `connect(
    // undefined, …)` and surfacing as a generic websocket failure.
    const pair = resolveLivekitPair(tokenInfo)
    const livekitRoom = await roomPromise
    // Remember the LiveKit host so the NEXT join can prewarm DNS/TLS in
    // parallel with the mint (see prepareConnection above). Compared against
    // the previous host every mint — a move is the exact diagnostic the
    // production investigation (MEET-LIVEKIT-PROJECT-MISMATCH-1) could not
    // read off the client, so it is logged, never silent.
    const hostCache = rememberPrewarmHost(window.localStorage, workspaceApiBase(), pair.url)
    if (hostCache.changed) {
      console.warn('[connectCall] livekit host moved since the last join', {
        previous: hostCache.previous,
        url: hostCache.url
      })
    }
    // RoomEvent comes from the same lazily-loaded module buildLivekitRoom just
    // imported (cached — this resolves instantly, no extra network round-trip).
    const { RoomEvent } = await import('livekit-client')
    // Stamp the workspace roomId on the instance — `room.name` is the
    // LiveKit-side room name which may differ from our meet_rooms id;
    // the room-switch check above reads this stamp first.
    livekitRoom._smblsRoomId = roomId

    const refresh = () => {
      _refreshParticipants(root, livekitRoom)
      // Schedule the DOM attach pass AFTER smbls has had a chance to
      // (re)render tiles for any newly-arrived participants. Without
      // the rAF deferral, the data-identity selector misses tiles that
      // smbls is still building from the just-updated livekitParticipants
      // array.
      requestAnimationFrame(() => attachAllParticipantTracks(livekitRoom))
    }
    // Participant-set events only — events that can change WHO is in the
    // room or what tracks exist.
    livekitRoom.on(RoomEvent.ParticipantConnected, refresh)
    livekitRoom.on(RoomEvent.ParticipantDisconnected, refresh)
    livekitRoom.on(RoomEvent.LocalTrackPublished, refresh)
    livekitRoom.on(RoomEvent.LocalTrackUnpublished, refresh)
    let firstRemoteAudio = false
    let firstRemoteVideo = false
    livekitRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (!firstRemoteAudio && track?.kind === 'audio') {
        firstRemoteAudio = true
        _mark('meetjoin:first-remote-audio')
        _measure('meetjoin:to-first-remote-audio', 'meetjoin:start', 'meetjoin:first-remote-audio')
      }
      if (!firstRemoteVideo && track?.kind === 'video') {
        firstRemoteVideo = true
        _mark('meetjoin:first-remote-video')
        _measure('meetjoin:to-first-remote-video', 'meetjoin:start', 'meetjoin:first-remote-video')
      }
      // Targeted attach FIRST — when the tile is already in the DOM the
      // media element gets the track THIS tick (remote audio starts playing
      // immediately) instead of waiting out the state-update + rAF
      // choreography. The refresh() below still covers the
      // tile-not-yet-rendered case (its rAF sweep attaches next frame).
      if (participant?.identity) attachParticipantTracks(livekitRoom, participant.identity, false)
      refresh()
    })
    livekitRoom.on(RoomEvent.TrackUnsubscribed, refresh)
    // Mute toggles change isMicEnabled/isCameraEnabled — also a full
    // refresh. These fire on user action, not per-frame, so the diff
    // stays stable.
    livekitRoom.on(RoomEvent.TrackMuted, refresh)
    livekitRoom.on(RoomEvent.TrackUnmuted, refresh)
    // ActiveSpeakersChanged fires on EVERY spoken syllable. Route to a
    // separate state slot (livekitSpeakers) so updating it doesn't
    // invalidate the livekitParticipants diff and tear down tile DOM.
    livekitRoom.on(RoomEvent.ActiveSpeakersChanged, () => _refreshSpeakers(root, livekitRoom))
    livekitRoom.on(RoomEvent.Disconnected, () => {
      console.log('[meet] livekit disconnected')
      _livekitRoom = null
      // A fly page on the stage belongs to this call (functions/meetFly.js).
      root.update({ livekitParticipants: [], livekitConnected: false, meetFly: null })
    })
    // FLY IN A CALL (functions/meetFly.js): the call's pages and its Fly
    // switch reach every member on the `symbols.fly` text-stream topic. The
    // listener is LOADED here, never looked up: connectCall rides the eager
    // slice and runs right after a router('/meet') from the new-call modal,
    // the chat composer, the calendar or a deep link — before the full meet
    // context is folded, when `this.call('meetFlyListen')` would be a silent
    // no-op and this member would hear no page and no switch for the whole
    // call. Awaited before connect(): a latecomer's replay arrives right after
    // the join. (The call chat asks again on mount; both are no-ops once
    // listening.)
    try {
      const { meetFlyListen } = await import('./meetFly.js')
      meetFlyListen.call(this, livekitRoom)
    } catch (err) {
      console.warn('[meet] fly listener not set:', err?.message || err)
    }
    // In-call text chat — payload is a JSON-encoded {text, senderName, sentAt}
    // object sent via LiveKit's data channel. Messages are appended to
    // root.meetChatMessages so MeetChatPanel can render them reactively.
    livekitRoom.on(RoomEvent.DataReceived, (payload, participant) => {
      try {
        const text = new TextDecoder().decode(payload)
        const msg = JSON.parse(text)
        if (!msg || typeof msg.text !== 'string') return
        const current = root.meetChatMessages || []
        // An AGENT's answer (functions/meetFly.js) carries its name and the flag.
        const agent = msg.agent === true
        const updates = {
          meetChatMessages: [
            ...current,
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
              text: msg.text,
              senderName:
                msg.senderName ||
                participant?.name ||
                participant?.identity ||
                this.call('polyglot', 'meet.chat.participant'),
              sentAt: msg.sentAt || new Date().toISOString(),
              isLocal: false,
              ...(agent ? { isAgent: true } : {})
            }
          ]
        }
        // Increment unread badge when the chat panel is closed.
        if (!root.showMeetChat) {
          updates.meetChatUnread = (root.meetChatUnread || 0) + 1
        }
        root.update(updates)
      } catch (err) {
        // Non-chat data packet — ignore silently.
      }
    })

    _mark('meetjoin:connect-start')
    await livekitRoom.connect(pair.url, pair.token)
    _mark('meetjoin:connected')
    _measure('meetjoin:connect', 'meetjoin:connect-start', 'meetjoin:connected')

    // Flip the UI to connected NOW — remote tiles, remote audio and the
    // call chrome must never wait behind the local getUserMedia/publish
    // leg (it used to: two serial enumerateDevices + two serial
    // getUserMedia calls ran BEFORE livekitConnected flipped, so every
    // already-subscribed remote track waited seconds to paint).
    // Tenant guard (MEET-LIVE-CALL-SURVIVES-SWITCH-1). The user can switch
    // workspaces anywhere in the join above — mint, import, permission prompt,
    // signal connect. A switch ran its own `disconnectCall` for whatever was
    // live AT THAT MOMENT, which cannot be this room: it finished connecting
    // afterwards. So the ONLY teardown this connection will ever get is here.
    // Adopt the module singleton and install the unload hook only AFTER the
    // write lands, so a dead-tenant join never becomes the live room.
    if (!_write({ livekitConnected: true, meetCallInFlight: false, meetChatMessages: [] })) {
      console.warn(
        '[connectCall] workspace changed during the join — dropping the connection made for the previous tenant'
      )
      _stopPreTracks()
      try {
        await livekitRoom.disconnect()
      } catch (err) {
        console.warn('[connectCall] late-join disconnect failed', err)
      }
      return null
    }
    _livekitRoom = livekitRoom
    _installUnloadHook()
    _refreshParticipants(root, livekitRoom)
    // Post-connect attach sweep — covers any remote tracks that were
    // already subscribed before we wired the RoomEvent listeners. Wait
    // a frame so smbls has rendered the tile DOM the selector needs.
    requestAnimationFrame(() => attachAllParticipantTracks(livekitRoom))

    // ── Local media ─────────────────────────────────────────────────────
    // Fast path: publish the tracks pre-acquired in parallel with the mint
    // (the Room's publishDefaults — simulcast layers per mode — apply to
    // publishTrack). Fallback (no devices / permission denied / partial
    // hardware): the per-device enable path with its full status taxonomy,
    // now with ONE shared enumerateDevices read and mic+cam enabled in
    // PARALLEL. Track per-device status into state so the call view can
    // surface a banner when the user blocked the browser permission —
    // without it a denied mic looks identical to a quiet mic and silently
    // breaks transcription.
    const _status = { mic: 'unknown', camera: 'unknown' }
    let localPublished = false
    const preTracks = await preTracksPromise
    if (Array.isArray(preTracks) && preTracks.length) {
      await Promise.all(
        preTracks.map(async (track) => {
          const key = track.kind === 'audio' ? 'mic' : 'camera'
          try {
            await livekitRoom.localParticipant.publishTrack(track)
            _status[key] = 'granted'
            if (!localPublished) {
              localPublished = true
              _mark('meetjoin:first-local-track')
            }
          } catch (err) {
            _status[key] = 'error'
            try {
              track.stop()
            } catch (_) {
              /* already stopped */
            }
            console.warn(`[connectCall] ${key} publish failed`, err)
          }
        })
      )
      // A kind absent from the acquired set: the user switched it off in
      // the join preview ('off'), or the device vanished between acquire
      // and publish — same bucket as not present ('unavailable').
      if (_status.mic === 'unknown') _status.mic = media.mic ? 'unavailable' : 'off'
      if (_status.camera === 'unknown') _status.camera = media.cam ? 'unavailable' : 'off'
    } else {
      // Skipping setEnabled when no devices are present avoids a
      // `NotFoundError` surfacing as a hard console.error on every connect
      // from headless browsers, dev VMs, or laptops with the lid closed.
      // Permission + genuinely-unexpected errors still log.
      const devices = await (async () => {
        try {
          return (await navigator.mediaDevices?.enumerateDevices?.()) || []
        } catch (err) {
          console.warn('[connectCall] enumerateDevices failed', err)
          return []
        }
      })()
      const _hasDevice = (kind) => devices.some((d) => d.kind === kind)
      const _enable = async (label, kind, fn) => {
        const key = label === 'mic' ? 'mic' : 'camera'
        if (!_hasDevice(kind)) {
          _status[key] = 'unavailable'
          return
        }
        try {
          await fn()
          _status[key] = 'granted'
          if (!localPublished) {
            localPublished = true
            _mark('meetjoin:first-local-track')
          }
        } catch (err) {
          const name = err?.name || ''
          if (name === 'NotFoundError') {
            _status[key] = 'unavailable'
            return
          }
          const reason = name === 'NotAllowedError' ? 'permission denied' : name || 'unknown'
          _status[key] = name === 'NotAllowedError' ? 'denied' : 'error'
          console.warn(`[connectCall] ${label} enable failed (${reason})`, err)
        }
      }
      // A kind the user switched off in the join preview is never enabled —
      // no getUserMedia, no permission prompt; its status reads 'off'.
      if (!media.mic) _status.mic = 'off'
      if (!media.cam) _status.camera = 'off'
      await Promise.all([
        media.mic
          ? _enable('mic', 'audioinput', () =>
              livekitRoom.localParticipant.setMicrophoneEnabled(true)
            )
          : Promise.resolve(),
        media.cam
          ? _enable('camera', 'videoinput', () =>
              livekitRoom.localParticipant.setCameraEnabled(true)
            )
          : Promise.resolve()
      ])
    }
    // The published tracks now belong to the room (disconnect stops them) —
    // don't let a later failure path stop live media.
    preTracksPromise = null
    if (localPublished) {
      _measure('meetjoin:to-first-local-track', 'meetjoin:start', 'meetjoin:first-local-track')
    }
    root.update({ meetMicStatus: _status.mic, meetCameraStatus: _status.camera })
    _mark('meetjoin:local-media-done')
    _measure('meetjoin:total', 'meetjoin:start', 'meetjoin:local-media-done')
    _refreshParticipants(root, livekitRoom)
    // Local tile attach — the mic/cam tracks published just above need the
    // same post-render frame wait as the remote sweep.
    requestAnimationFrame(() => attachAllParticipantTracks(livekitRoom))
    // Backfill existing utterances for this room so a late-joiner sees
    // the running transcript instead of an empty captions overlay.
    // realtime sub continues to deliver INSERTs after this.
    try {
      const sdk = getSDK()
      const res = await sdk.execute('workspaceProject.meet.utterances', 'list', { roomId })
      const rows = res?.data || res || []
      if (Array.isArray(rows) && rows.length) {
        const utts = rows.map(rowToUtterance)
        const existing = root.utterances || []
        const seen = new Set(existing.map((u) => u.id))
        const merged = [...existing]
        for (const u of utts)
          if (u && !seen.has(u.id)) {
            seen.add(u.id)
            merged.push(u)
          }
        root.update({ utterances: merged })
      }
    } catch (err) {
      console.warn('[connectCall] utterance backfill failed', err?.message || err)
    }
    return livekitRoom
  } catch (err) {
    // MEET-ERROR-COPY-LIES-1: this already logs the full raw error object
    // (not just the message) — keep it that way.
    //
    // MEET-CALL-SERVER-UNREACHABLE-1: the state carried only message/name/
    // code, and code is a constant 1 for every livekit-client
    // ConnectionError — so the overlay downstream had nothing to classify
    // with and called every failure a "hiccup" with a Retry button. The
    // edge's own verdict (reasonName + the HTTP status it answered
    // /rtc/validate with, e.g. 429 when the call service is at its capacity
    // limit) was on the error object and was thrown away here.
    // `livekitErrorFields` carries it over — it still infers nothing.
    console.error('[connectCall] failed', err)
    _stopPreTracks()
    _livekitRoom = null
    let _errFields = livekitErrorFields(err)
    // livekit-client drops the edge's HTTP status for every signal failure
    // that is not 404/401/403 — a capacity refusal arrives as a bare
    // ServerUnreachable with no status (measured 2026-08-21). Re-run the
    // SDK's own validate request, ONCE, to recover it. Only the statuses
    // that name a real refusal are adopted: a 404 from the probe only means
    // the room is not live yet, which is not why this join failed.
    if (_errFields.status === null && /signal connection|websocket/i.test(_errFields.message)) {
      const _probed = await probeLivekitStatus(_tokenInfo?.livekitUrl, _tokenInfo?.livekitToken)
      if (_probed === 429 || _probed === 401 || _probed === 403) {
        console.warn('[connectCall] livekit edge answered', _probed, 'to the validate probe')
        _errFields = { ..._errFields, status: _probed }
      }
    }
    // Same guard, same reason (MEET-LIVE-CALL-SURVIVES-SWITCH-1): a failure
    // from A's room must not paint "Couldn't join: …" over B. The five error
    // keys stay ONE co-written record — the guard drops the whole record or
    // lands the whole record, never half of it. Nothing to disconnect on this
    // path: the connect never completed.
    if (
      !_write({
        meetCallError: _errFields.message,
        meetCallErrorName: _errFields.name,
        meetCallErrorCode: _errFields.code,
        meetCallErrorReason: _errFields.reasonName,
        meetCallErrorStatus: _errFields.status,
        meetCallInFlight: false,
        livekitConnected: false
      })
    ) {
      console.warn(
        '[connectCall] workspace changed during the join — dropping the error of the previous tenant'
      )
    }
    return null
  }
}

// Send a chat message via LiveKit data channel. Appends it to
// root.meetChatMessages as a local (isLocal: true) message immediately
// so the sender sees their own message without waiting for the echo.
// Reliability: RELIABLE mode, broadcast (no specific participant target).
// `opts.as` + `opts.agent` (functions/meetFly.js): a mentioned AGENT's answer,
// sent from its asker's client under the agent's name.
export const sendMeetChatMessage = async function (text, opts) {
  const root = this.state.root
  const room = _livekitRoom
  if (!room) {
    console.warn('[sendMeetChatMessage] not connected')
    return
  }
  const trimmed = (text || '').trim()
  if (!trimmed) return
  const me = (root.livekitParticipants || []).find((p) => p.isLocal)
  const agent = !!(opts && opts.agent && opts.as)
  const senderName = agent
    ? String(opts.as)
    : me?.name || room.localParticipant?.name || room.localParticipant?.identity || 'Me'
  const sentAt = new Date().toISOString()
  const payload = JSON.stringify({
    text: trimmed,
    senderName,
    sentAt,
    ...(agent ? { agent: true } : {})
  })
  // Optimistic local append
  const current = root.meetChatMessages || []
  root.update({
    meetChatMessages: [
      ...current,
      {
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text: trimmed,
        senderName,
        sentAt,
        // An agent's answer is not the sender's own line.
        isLocal: !agent,
        ...(agent ? { isAgent: true } : {})
      }
    ]
  })
  try {
    const encoded = new TextEncoder().encode(payload)
    await room.localParticipant.publishData(encoded, { reliable: true })
  } catch (err) {
    console.error('[sendMeetChatMessage] publishData failed', err)
  }
}

export const disconnectCall = async function () {
  const root = this.state.root
  // Tear down the CALL-slot subscription — the only place it ends. The
  // page slot (list/transcript views) is untouched; whatever page is on
  // screen keeps its own scope.
  try {
    unsubscribeMeetCallRealtime()
  } catch (err) {
    console.warn('[disconnectCall] call-slot unsubscribe failed', err)
  }
  const room = _livekitRoom
  if (!room) {
    root.update({ livekitParticipants: [], livekitConnected: false })
    return
  }
  try {
    await room.disconnect()
  } catch (err) {
    console.warn('[disconnectCall] failed', err)
  }
  _livekitRoom = null
  root.update({ livekitParticipants: [], livekitConnected: false })
}
